import { create } from 'zustand';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  setDoc,
  deleteDoc,
  query,
  limit,
} from '@/lib/firebase/firestore';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db, functions, rtdb } from '@/config/firebase';
import { ref as rtdbRef, set as rtdbSet, remove as rtdbRemove } from 'firebase/database';
import { httpsCallable } from '@/lib/firebase/functions';
import { useAuthStore, getEffectiveUid } from './auth-store';
import { useAppStore } from './app-store';
import { idbCache } from '@/lib/cache/idb-cache';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppUser {
  id: string;
  name: string;
  email: string;
  picture?: string;
  saasOrgID?: string;
  createdTime?: number;
  invitedAdminUserID?: string;
  invitedAdminUserEmail?: string;
  invitedAdminUserName?: string;
  invitedAdminUserPic?: string;
  addedAdminSharedUserInPage?: boolean;
  userAddedByEmail?: boolean;
  /** `'rejected'` once the invited user declines. Written by their client
   *  (auth-store `rejectAdminInvite`) so this list can show the outcome
   *  without a per-member read. Cleared by a re-add, which rewrites the doc. */
  inviteStatus?: 'rejected';
  rejectedTime?: number;
}

/** Cached Firebase user details fetched via cloud function */
export interface UserDetails {
  name?: string;
  email?: string;
  photoURL?: string;
  uid?: string;
}

interface AppUsersStore {
  // Data
  orgUsers: Record<string, AppUser>;
  /** Cache of user details fetched by email/uid — keyed by email */
  userDetailsCache: Record<string, UserDetails>;
  loading: boolean;
  /** True once loadOrgUsers has completed at least once */
  loaded: boolean;
  /** True when the last Firestore fetch errored — orgUsers may then be empty/stale, not authoritative */
  orgUsersLoadFailed: boolean;
  actionInProgress: Record<string, boolean>;
  searchTerm: string;

  // Actions
  /** `force` bypasses the once-per-page-load latch AND the SDK's persistent
   *  cache (server read) — for callers about to make destructive decisions
   *  from org membership, e.g. the Call Agents popup pruning stale agents. */
  loadOrgUsers: (opts?: { force?: boolean }) => Promise<void>;
  /** Fill in `inviteStatus` / `rejectedTime` on the already-loaded list.
   *  One Firestore read per member, so call it only from a screen that shows
   *  the outcome — not from app-init. No-op until `loadOrgUsers` has data. */
  refreshInviteStatuses: () => Promise<void>;
  addUserToOrg: (user: AppUser) => Promise<void>;
  removeUserFromOrg: (userId: string, email: string) => Promise<void>;
  addUserByEmail: (name: string, email: string) => Promise<void>;
  setSearchTerm: (term: string) => void;
  /** Fetch user details (name, photoURL) by email via cloud function. Results are cached. */
  fetchUserDetails: (email: string) => Promise<UserDetails | null>;
  /** Get cached user details instantly (no fetch). Returns null if not cached. */
  getUserDetailsInstant: (email: string) => UserDetails | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getExtensionName(): string {
  return useAppStore.getState().extensionName || '';
}

function getOrgId(): string {
  return useAppStore.getState().appConfig?.orgId || '';
}

function getCurrentUser() {
  return useAuthStore.getState().user;
}

/**
 * Firestore paths matching the old app:
 *   ulgebraUsers/{currentUserUID}/extensions/{extensionName}/uaOrgUsers/{userEmail}
 *   uaOrgUsersEmailMap/{userEmail}/extensions/{extensionName}/uaOrgAdmins/{currentUserUID}
 *
 * @param forRead — when true, uses effective UID (impersonated user) for reads
 */
function getOrgUsersColPath(forRead = false): string {
  const uid = (forRead ? getEffectiveUid() : getCurrentUser()?.uid) || '';
  const ext = getExtensionName();
  return `ulgebraUsers/${uid}/extensions/${ext}/uaOrgUsers`;
}

function getEmailMapColPath(userEmail: string): string {
  const ext = getExtensionName();
  return `uaOrgUsersEmailMap/${userEmail}/extensions/${ext}/uaOrgAdmins`;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/** Track in-flight fetches to avoid duplicate calls for the same email */
const _pendingFetches: Record<string, Promise<UserDetails | null>> = {};

/** One Firestore refresh of the org-users list per page load. The IDB cache
 *  can serve any number of callers; only the first goes to the network. */
let _orgUsersRevalidated = false;
/** A force load arrived while a pass was in flight — re-run when it settles,
 *  otherwise a cache-served pass could masquerade as the server-confirmed
 *  read the force caller (destructive agent filtering) depends on. */
let _pendingForceLoad = false;

/** Guards the invite-status sweep against overlapping runs. */
let _inviteStatusesInFlight = false;

/** The shared `{tpa}forpage` appCode a page-family add also dual-writes to,
 *  or null when `ext` isn't page-family (or already IS the page appCode).
 *  Same regex `addUserToOrg` / `removeUserFromOrg` gate their dual-write on. */
function pageExtFor(ext: string): string | null {
  const m = /^(whatsapp|messagebird|twilio|routemobilewa)for/.exec(ext);
  if (!m) return null;
  const pageExt = `${m[1]}forpage`;
  return ext === pageExt ? null : pageExt;
}

/**
 * Fill in `inviteStatus` from each member's own invite doc.
 *
 * The rejection is written by the invited user, and the only copy they can
 * write is the one under their own email:
 *   `uaOrgUsersEmailMap/{email}/extensions/{ext}/uaOrgAdmins/{adminUid}`
 * `ulgebraUsers/{adminUid}/**` is the admin's credential tree, so a user
 * can't mark themselves rejected there — which is why the status can't just
 * ride along on the org-users doc this list already reads.
 *
 * Checks BOTH appCodes, first match wins. An invite added through the
 * standalone page flow exists only under `{tpa}forpage` — the same case
 * `fetchInvitedAdminList` falls back for — so the user's `rejectAdminInvite`
 * can only land the mark there. Reading `ext` alone missed every one of those.
 *
 * Cost is one read per member (two only for members whose invite isn't on the
 * primary appCode), bounded by the same limit(200) as the list. Runs from the
 * Organization settings screen, never on every page load. Chunked so a large
 * org doesn't open 200 reads at once.
 */
async function mergeInviteRejections(users: Record<string, AppUser>, ext: string): Promise<void> {
  const adminUid = getEffectiveUid();
  const emails = Object.keys(users);
  if (!adminUid || !ext || emails.length === 0) return;

  const pageExt = pageExtFor(ext);
  const appCodes = pageExt ? [ext, pageExt] : [ext];

  const CHUNK = 20;
  let failed = 0;
  for (let i = 0; i < emails.length; i += CHUNK) {
    const chunk = emails.slice(i, i + CHUNK);
    const results = await Promise.allSettled(chunk.map(async (email) => {
      for (const appCode of appCodes) {
        const snap = await getDoc(
          doc(db, `uaOrgUsersEmailMap/${email}/extensions/${appCode}/uaOrgAdmins`, adminUid)
        );
        // No invite on this appCode — the add may only have written the other.
        if (!snap.exists()) continue;
        const d = snap.data();
        // Found the live invite. It is authoritative in BOTH directions: the
        // caller may hand us a member who already carries a badge (from the
        // cached paint or an earlier sweep), and a rejection undone by a re-add
        // has to actually clear. A read that throws never gets here — the
        // allSettled below drops it and the previous value stands.
        const rejected = d.inviteStatus === 'rejected';
        users[email].inviteStatus = rejected ? 'rejected' : undefined;
        users[email].rejectedTime = rejected && typeof d.rejectedTime === 'number' ? d.rejectedTime : undefined;
        // First match wins — don't let a stale copy on the other appCode
        // override the live one.
        return;
      }
    }));
    failed += results.filter((r) => r.status === 'rejected').length;
  }

  const rejected = emails.filter((e) => users[e].inviteStatus === 'rejected').length;
  console.log(
    `[app-users-store] invite status → ${rejected} rejected of ${emails.length}` +
    (failed ? ` (${failed} reads failed)` : '')
  );
}

/**
 * Fire-and-forget CRM appUsCount sync. Skipped when an admin is impersonating
 * another user, since the orgUsers count belongs to the effective uid but the
 * write target should be the logged-in user's profile.
 */
async function syncAppUsCountToCRM(count: number) {
  try {
    const uid = useAuthStore.getState().user?.uid;
    if (!uid || uid !== getEffectiveUid()) return;
    const { syncAppUserCount } = await import('@/lib/crm-sync');
    await syncAppUserCount(uid, count);
  } catch (e) {
    console.warn('[app-users-store] CRM appUsCount sync failed:', e);
  }
}

export const useAppUsersStore = create<AppUsersStore>((set, get) => ({
  orgUsers: {},
  userDetailsCache: {},
  loading: false,
  loaded: false,
  orgUsersLoadFailed: false,
  actionInProgress: {},
  searchTerm: '',

  // -------------------------------------------------------------------------
  // loadOrgUsers — fetch users added by admin from Firestore
  // -------------------------------------------------------------------------
  loadOrgUsers: async (opts) => {
    // Guards an infinite loop when 0 org users exist, and lets the many
    // callers share one network pass per page load.
    if (get().loading) {
      if (opts?.force) _pendingForceLoad = true;
      return;
    }
    if (!opts?.force && get().loaded && _orgUsersRevalidated) return;

    const colPath = getOrgUsersColPath(true); // read path — uses effective UID
    if (!colPath || colPath.includes('//')) return;

    // Claim the pass BEFORE the first await. app-users-settings,
    // group-members-tab, twilio-sink-admin-card, use-org-agents, voice-settings
    // and app-init all call this on mount; the guards above are still open
    // while the IDB read below is pending, so without a synchronous claim each
    // of them opens its own Firestore pass.
    set({ loading: true });

    const ext = getExtensionName();
    const idbKey = ext ? `org_users_${ext}` : '';

    // Paint from cache immediately, then keep going and refresh from
    // Firestore behind it.
    //
    // This used to `return` on a cache hit, so for up to 24h the list showed
    // only what THIS admin last wrote. Anything changed by anyone else never
    // arrived — most visibly a user rejecting their invite, but equally a
    // removal done from another device.
    //
    // Wrapped: `loading` is already true by this point, so an escaping
    // rejection here (idbCache.get swallows most errors, but a synchronous
    // `db.transaction` throw resolves as a rejected promise) would strand the
    // flag and the `if (get().loading) return` guard would kill every retry
    // for the rest of the session. A cache miss is not worth that.
    if (!get().loaded && idbKey) {
      try {
        const idbCached = await idbCache.get<Record<string, AppUser>>('org_data', idbKey);
        if (idbCached && Object.keys(idbCached).length > 0) {
          console.log(`[app-users-store] IDB HIT → ${Object.keys(idbCached).length} org users (refreshing in background)`);
          set({ orgUsers: idbCached, loaded: true });
        }
      } catch (e) {
        console.warn('[app-users-store] IDB read failed, going straight to Firestore:', e);
      }
    }

    _orgUsersRevalidated = true;
    try {
      // force = a server-confirmed read: the SDK's persistent cache can hand
      // getDocs a stale population from a previous session, which must not
      // feed destructive filtering (a Save pruning agents).
      const snapshot = opts?.force
        ? await getDocsFromServer(query(collection(db, colPath), limit(200)))
        : await getDocs(query(collection(db, colPath), limit(200)));
      const users: Record<string, AppUser> = {};

      const toAppUser = (email: string, data: Record<string, unknown>): AppUser => ({
        id: (data.id as string) || email.replace(/@/g, '__').replace(/\./g, '_'),
        name: (data.name as string) || '',
        email,
        picture: (data.invitedAdminUserPic as string) || undefined,
        saasOrgID: (data.saasOrgID as string) || getOrgId(),
        createdTime: data.createdTime as AppUser['createdTime'],
        invitedAdminUserID: data.invitedAdminUserID as AppUser['invitedAdminUserID'],
        invitedAdminUserEmail: data.invitedAdminUserEmail as AppUser['invitedAdminUserEmail'],
        invitedAdminUserName: data.invitedAdminUserName as AppUser['invitedAdminUserName'],
        addedAdminSharedUserInPage: data.addedAdminSharedUserInPage as AppUser['addedAdminSharedUserInPage'],
        userAddedByEmail: data.userAddedByEmail as AppUser['userAddedByEmail'],
        inviteStatus: data.inviteStatus === 'rejected' ? 'rejected' : undefined,
        rejectedTime: typeof data.rejectedTime === 'number' ? data.rejectedTime : undefined,
      });

      snapshot.docs.forEach((d: QueryDocumentSnapshot<DocumentData>) => {
        users[d.id] = toAppUser(d.id, d.data());
      });

      // Union the shared `{tpa}forpage` population: a page-family add
      // dual-writes there, so an agent invited via the standalone page flow
      // may have a uaOrgUsers doc ONLY under the page appCode. They register
      // and ring (the backend token check accepts that claim), so leaving them
      // out of the valid set would make a Save silently prune a real agent.
      // Current-ext entries win on collision; a page-ext read failure is
      // non-fatal — the primary list already loaded.
      const pageExt = pageExtFor(ext);
      if (pageExt) {
        try {
          const pageUid = getEffectiveUid() || '';
          const pagePath = `ulgebraUsers/${pageUid}/extensions/${pageExt}/uaOrgUsers`;
          if (pageUid && !pagePath.includes('//')) {
            const pageSnap = opts?.force
              ? await getDocsFromServer(query(collection(db, pagePath), limit(200)))
              : await getDocs(query(collection(db, pagePath), limit(200)));
            pageSnap.docs.forEach((d: QueryDocumentSnapshot<DocumentData>) => {
              if (!users[d.id]) users[d.id] = toAppUser(d.id, d.data());
            });
          }
        } catch (e) {
          console.warn('[app-users-store] page-ext org users union skipped:', e);
        }
      }

      // NOTE: who has declined is deliberately NOT merged here. It costs one
      // read per member (up to 200) and this runs from the app-init background
      // tier on every page load, for admins who never open the members list.
      // `refreshInviteStatuses` does it from the screen that shows the badge.
      // Awaiting it here also held the whole list back for ~10 sequential
      // round-trips on a cold load.

      // Persist to IDB with 24h TTL
      if (idbKey) {
        idbCache.set('org_data', idbKey, users, 24 * 60 * 60 * 1000).catch(() => {});
      }
      set({ orgUsers: users, loading: false, loaded: true, orgUsersLoadFailed: false });
      void syncAppUsCountToCRM(Object.keys(users).length);
    } catch (error) {
      console.error('[app-users-store] loadOrgUsers error:', error);
      set({ loading: false, loaded: true, orgUsersLoadFailed: true });
    }

    if (_pendingForceLoad) {
      _pendingForceLoad = false;
      void get().loadOrgUsers({ force: true });
    }
  },

  // -------------------------------------------------------------------------
  // refreshInviteStatuses — who has declined their invite
  // -------------------------------------------------------------------------
  refreshInviteStatuses: async () => {
    if (_inviteStatusesInFlight) return;
    // A load in flight ends with `set({ orgUsers })`, which would overwrite the
    // badges we're about to apply. Callers re-run this on the loading→false
    // edge, so bailing here just defers the sweep, it doesn't skip it.
    if (get().loading) return;

    const ext = getExtensionName();
    const current = get().orgUsers;
    if (!ext || Object.keys(current).length === 0) return;

    _inviteStatusesInFlight = true;
    try {
      // Merge into a copy: an add/remove can land while the reads are in
      // flight, and writing the pre-read snapshot back would undo it.
      const merged: Record<string, AppUser> = {};
      for (const [email, u] of Object.entries(current)) merged[email] = { ...u };
      await mergeInviteRejections(merged, ext);

      set((s) => {
        const next: Record<string, AppUser> = { ...s.orgUsers };
        let changed = false;
        for (const email of Object.keys(next)) {
          const m = merged[email];
          // Skip members added after the sweep started — we never read them.
          if (!m) continue;
          if (next[email].inviteStatus === m.inviteStatus) continue;
          next[email] = { ...next[email], inviteStatus: m.inviteStatus, rejectedTime: m.rejectedTime };
          changed = true;
        }
        if (!changed) return {};

        // Keep the cached paint in sync so the badge survives a reload.
        const idbKey = ext ? `org_users_${ext}` : '';
        if (idbKey) {
          idbCache.set('org_data', idbKey, next, 24 * 60 * 60 * 1000).catch(() => {});
        }
        return { orgUsers: next };
      });
    } catch (error) {
      console.warn('[app-users-store] refreshInviteStatuses failed:', error);
    } finally {
      _inviteStatusesInFlight = false;
    }
  },

  // -------------------------------------------------------------------------
  // addUserToOrg — add a CRM user to the workspace
  // -------------------------------------------------------------------------
  addUserToOrg: async (user: AppUser) => {
    const currentUser = getCurrentUser();
    if (!currentUser || user.email === currentUser.email) return;

    set((s) => ({ actionInProgress: { ...s.actionInProgress, [user.id]: true } }));

    try {
      const orgId = getOrgId();
      const ext = getExtensionName();

      const effectiveUid = getEffectiveUid() || '';

      const objToSave: Record<string, unknown> = {
        id: user.id,
        name: user.name || 'null',
        email: user.email,
        saasOrgID: orgId,
        createdTime: Date.now(),
        invitedAdminUserID: effectiveUid,
        invitedAdminUserEmail: currentUser.email,
        invitedAdminUserName: currentUser.displayName,
        invitedAdminUserPic: currentUser.photoURL || 'null',
        addedAdminSharedUserInPage: true,
        // A fresh add is an unaccepted invite. `setDoc` replaces the doc, so a
        // re-add after a removal already drops any earlier `currentSelected:
        // true` — writing it explicitly keeps that intent visible rather than
        // depending on the absence of a field.
        currentSelected: false,
      };

      // Sanitize nulls the same way old app does
      Object.keys(objToSave).forEach((key) => {
        if (objToSave[key] === null || objToSave[key] === undefined) {
          objToSave[key] = 'null';
        }
      });

      // Write to both Firestore locations (dual-write for fast lookup)
      const userDocPath = `ulgebraUsers/${effectiveUid}/extensions/${ext}/uaOrgUsers`;
      const emailMapPath = `uaOrgUsersEmailMap/${user.email}/extensions/${ext}/uaOrgAdmins`;

      await Promise.all([
        setDoc(doc(db, userDocPath, user.email), objToSave),
        setDoc(doc(db, emailMapPath, effectiveUid), objToSave),
      ]);

      // Also write to *forpage variant if applicable (matches old app)
      if (/^(whatsapp|messagebird|twilio|routemobilewa)for/.test(ext)) {
        const tpa = ext.split('for')[0];
        const pageExt = `${tpa}forpage`;
        const pageUserDocPath = `ulgebraUsers/${effectiveUid}/extensions/${pageExt}/uaOrgUsers`;
        const pageEmailMapPath = `uaOrgUsersEmailMap/${user.email}/extensions/${pageExt}/uaOrgAdmins`;

        await Promise.all([
          setDoc(doc(db, pageUserDocPath, user.email), objToSave),
          setDoc(doc(db, pageEmailMapPath, effectiveUid), objToSave),
        ]);

        // Write RTDB entry so the CF can verify permission when shared user requests token claims
        const normalizedEmail = user.email.replaceAll('.', '_');
        const rtdbPath = `user_instant_notification_receivers/${effectiveUid}/${pageExt}/teams/${normalizedEmail}/ct`;
        await rtdbSet(rtdbRef(rtdb, rtdbPath), Date.now());
      }

      // Update local state + IDB cache
      set((s) => {
        const updatedUsers = {
          ...s.orgUsers,
          [user.email]: {
            ...user,
            saasOrgID: orgId,
            createdTime: Date.now(),
            invitedAdminUserID: effectiveUid,
            invitedAdminUserEmail: currentUser.email,
            invitedAdminUserName: currentUser.displayName,
            addedAdminSharedUserInPage: true,
            // The doc was replaced, so any earlier rejection is gone. Drop it
            // here too, otherwise a re-added user keeps the "Rejected" badge
            // until the next full reload.
            inviteStatus: undefined,
            rejectedTime: undefined,
          },
        };
        // Sync IDB cache so next reload doesn't need Firestore
        const idbKey = ext ? `org_users_${ext}` : '';
        if (idbKey) {
          idbCache.set('org_data', idbKey, updatedUsers, 24 * 60 * 60 * 1000).catch(() => {});
        }

        return {
          orgUsers: updatedUsers,
          actionInProgress: { ...s.actionInProgress, [user.id]: false },
        };
      });
    } catch (error) {
      console.error('[app-users-store] addUserToOrg error:', error);
      set((s) => ({ actionInProgress: { ...s.actionInProgress, [user.id]: false } }));
    }
  },

  // -------------------------------------------------------------------------
  // removeUserFromOrg — remove a user from the workspace
  // -------------------------------------------------------------------------
  removeUserFromOrg: async (userId: string, email: string) => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    set((s) => ({ actionInProgress: { ...s.actionInProgress, [userId]: true } }));

    try {
      const ext = getExtensionName();
      const effectiveUid = getEffectiveUid() || '';
      const userDocPath = `ulgebraUsers/${effectiveUid}/extensions/${ext}/uaOrgUsers`;
      const emailMapPath = `uaOrgUsersEmailMap/${email}/extensions/${ext}/uaOrgAdmins`;

      await Promise.all([
        deleteDoc(doc(db, userDocPath, email)),
        deleteDoc(doc(db, emailMapPath, effectiveUid)),
      ]);

      // Also remove from *forpage variant if applicable
      if (/^(whatsapp|messagebird|twilio|routemobilewa)for/.test(ext)) {
        const tpa = ext.split('for')[0];
        const pageExt = `${tpa}forpage`;
        const pageUserDocPath = `ulgebraUsers/${effectiveUid}/extensions/${pageExt}/uaOrgUsers`;
        const pageEmailMapPath = `uaOrgUsersEmailMap/${email}/extensions/${pageExt}/uaOrgAdmins`;

        await Promise.all([
          deleteDoc(doc(db, pageUserDocPath, email)),
          deleteDoc(doc(db, pageEmailMapPath, effectiveUid)),
        ]);

        // Remove RTDB permission entry
        const normalizedEmail = email.replaceAll('.', '_');
        const rtdbPath = `user_instant_notification_receivers/${effectiveUid}/${pageExt}/teams/${normalizedEmail}`;
        await rtdbRemove(rtdbRef(rtdb, rtdbPath));
      }

      // Clear the removed user's token claims so they lose API access immediately.
      // The CF reads adminId from token claims as a fallback — without clearing,
      // the removed user retains access permanently (custom claims are persistent).
      //
      // `userId` is the id the admin's org-users doc was keyed by — a CRM user
      // id, or the email with @/. substituted. Neither is a Firebase UID, so
      // sending it as `targetUserUid` never matched an account and the claims
      // survived the removal. Resolve the real UID from the email first
      // (cached in-memory + IDB, so usually free) and only fall back to the
      // old value when the lookup can't answer.
      const tpa = ext.includes('for') ? ext.split('for')[0] : ext;
      const pageAppCode = `${tpa}forpage`;
      const appCodes = ext === pageAppCode ? [pageAppCode] : [ext, pageAppCode];
      const callCF = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      get().fetchUserDetails(email)
        .catch(() => null)
        .then((details) => {
          const targetUserUid = details?.uid || userId;
          if (!details?.uid) {
            console.warn('[app-users-store] Could not resolve Firebase UID for', email, '— clearing claims with', userId);
          }
          // Sequential, NOT Promise.all: the CF read-modify-writes the whole
          // customClaims object, so parallel calls clobber each other's
          // deletion and one appCode's adminId survives. Same reason as the
          // clear branch in auth-store's setSelectedAdmin.
          return appCodes.reduce<Promise<unknown>>(
            (chain, appCode) => chain.then(() => callCF({
              appCode,
              TPAService: tpa,
              action: 'CLEAR_USER_TOKEN_CLAIMS',
              targetUserUid,
              headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
            })),
            Promise.resolve(),
          );
        })
        .catch((err) => {
          console.warn('[app-users-store] Failed to clear removed user token claims:', err);
        });

      // Update local state + IDB cache
      set((s) => {
        const updated = { ...s.orgUsers };
        delete updated[email];
        // Sync IDB cache so next reload doesn't need Firestore
        const idbKey = ext ? `org_users_${ext}` : '';
        if (idbKey) {
          idbCache.set('org_data', idbKey, updated, 24 * 60 * 60 * 1000).catch(() => {});
        }
        return {
          orgUsers: updated,
          actionInProgress: { ...s.actionInProgress, [userId]: false },
        };
      });
    } catch (error) {
      console.error('[app-users-store] removeUserFromOrg error:', error);
      set((s) => ({ actionInProgress: { ...s.actionInProgress, [userId]: false } }));
    }
  },

  // -------------------------------------------------------------------------
  // addUserByEmail — manually add a user by email (not from CRM list)
  // -------------------------------------------------------------------------
  addUserByEmail: async (name: string, email: string) => {
    if (!email) return;

    const id = email.replace(/@/g, '__').replace(/\./g, '_');
    const user: AppUser = {
      id,
      name: name || email,
      email,
      saasOrgID: getOrgId(),
      userAddedByEmail: true,
    };

    await get().addUserToOrg(user);
  },

  setSearchTerm: (term: string) => set({ searchTerm: term }),

  // -------------------------------------------------------------------------
  // fetchUserDetails — fetch Firebase user details by email (with cache)
  // Mirrors old app's getFBUserByEmailOrUID
  // -------------------------------------------------------------------------
  fetchUserDetails: async (email: string) => {
    if (!email || email === 'unassigned') return null;

    // Return from in-memory store cache if available
    const cached = get().userDetailsCache[email];
    if (cached) return cached;

    // Check IDB cache before making a Cloud Function call
    const idbCached = await idbCache.get<UserDetails>('org_data', `user_detail_${email}`);
    if (idbCached) {
      // Populate in-memory store cache
      set((s) => ({
        userDetailsCache: { ...s.userDetailsCache, [email]: idbCached },
      }));
      return idbCached;
    }

    // Deduplicate in-flight requests
    const pending = _pendingFetches[email];
    if (pending) return await pending;

    const extensionName = getExtensionName();
    if (!extensionName) return null;

    const promise = (async (): Promise<UserDetails | null> => {
      try {
        const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
        const resp = await callServiceAPI({
          appCode: extensionName,
          TPAService: extensionName.split('for')[0],
          action: 'GET_USERDETAILS_FOR_FBUID',
          uaFBUserID: null,
          uaFBEmail: email,
          headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
        });

        const data = resp.data as { status?: string; userResponse?: UserDetails } | undefined;
        if (data?.status === 'success' && data.userResponse) {
          const details: UserDetails = {
            name: data.userResponse.name || undefined,
            email: data.userResponse.email || email,
            photoURL: data.userResponse.photoURL || undefined,
            uid: data.userResponse.uid || undefined,
          };
          // Cache in store + IDB (24h TTL)
          set((s) => {
            return { userDetailsCache: { ...s.userDetailsCache, [email]: details } };
          });
          idbCache.set('org_data', `user_detail_${email}`, details, 24 * 60 * 60 * 1000).catch(() => {});
          return details;
        }
      } catch (error) {
        console.error('[app-users-store] fetchUserDetails error:', error);
      }
      return null;
    })();

    _pendingFetches[email] = promise;
    const result = await promise;
    delete _pendingFetches[email];
    return result;
  },

  getUserDetailsInstant: (email: string) => {
    return get().userDetailsCache[email] || null;
  },
}));
